import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Mail } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import RetailerLayout from "@/components/RetailerLayout";

/**
 * Admin-only email preview panel. Lets ops proof every transactional
 * template with sample data without triggering a real reservation.
 *
 * Uses the backend's `capture` mode — the exact Brevo payload that WOULD
 * be sent is returned as JSON, then rendered here in an isolated iframe.
 */
export default function AdminEmailPreview() {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("html"); // "html" | "text"

  useEffect(() => {
    api
      .get("/admin/email-preview")
      .then((r) => {
        setTemplates(r.data.templates || []);
        if ((r.data.templates || []).length > 0) {
          setSelected(r.data.templates[0].kind);
        }
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    api
      .get(`/admin/email-preview/${selected}`)
      .then((r) => setPreview(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [selected]);

  return (
    <RetailerLayout>
      <main
        className="max-w-6xl mx-auto px-4 sm:px-6 py-6"
        data-testid="admin-email-preview-page"
      >
        <div className="flex items-center gap-2 mb-4 text-sm">
          <Link
            to="/admin"
            className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900"
            data-testid="email-preview-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Back to admin
          </Link>
        </div>
        <div className="flex items-center gap-3 mb-6">
          <Mail className="w-5 h-5 text-slate-500" />
          <div>
            <h1 className="font-display font-bold text-2xl text-slate-900">
              Email preview
            </h1>
            <p className="text-sm text-slate-500">
              Renders every transactional email exactly as Brevo would receive
              it, with sample data. No dispatch, no database writes.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
          {/* Kind selector */}
          <aside className="lg:sticky lg:top-4 self-start">
            <ul className="space-y-1" data-testid="email-preview-kinds">
              {templates.map((t) => (
                <li key={t.kind}>
                  <button
                    type="button"
                    onClick={() => setSelected(t.kind)}
                    className={
                      "w-full text-left px-3 py-2 rounded-md text-sm transition " +
                      (selected === t.kind
                        ? "bg-emerald-900 text-white"
                        : "hover:bg-slate-100 text-slate-700")
                    }
                    data-testid={`email-preview-kind-${t.kind}`}
                  >
                    {t.label}
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          {/* Preview panel */}
          <section
            className="bg-white border border-slate-200 rounded-xl overflow-hidden"
            data-testid="email-preview-panel"
          >
            {loading && (
              <div className="p-6 text-sm text-slate-500">Loading preview…</div>
            )}
            {!loading && preview && (
              <>
                <div className="border-b border-slate-200 p-4 text-sm space-y-1">
                  <div>
                    <span className="text-slate-500">Subject:</span>{" "}
                    <span
                      className="font-medium text-slate-900"
                      data-testid="email-preview-subject"
                    >
                      {preview.subject}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">From:</span>{" "}
                    <span data-testid="email-preview-sender">
                      {preview.sender?.name} &lt;{preview.sender?.email}&gt;
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">To (sample):</span>{" "}
                    <span data-testid="email-preview-recipient">
                      {(preview.to || [])
                        .map((r) => `${r.name ? r.name + " " : ""}<${r.email}>`)
                        .join(", ")}
                    </span>
                  </div>
                  {preview.tags?.length > 0 && (
                    <div>
                      <span className="text-slate-500">Tags:</span>{" "}
                      {preview.tags.map((t) => (
                        <span
                          key={t}
                          className="inline-block bg-slate-100 text-slate-700 rounded-full px-2 py-0.5 mr-1 text-xs"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-2 bg-slate-50">
                  <button
                    type="button"
                    onClick={() => setView("html")}
                    className={
                      "text-sm px-3 py-1 rounded " +
                      (view === "html"
                        ? "bg-white border border-slate-300"
                        : "text-slate-500 hover:text-slate-800")
                    }
                    data-testid="email-preview-view-html"
                  >
                    HTML
                  </button>
                  <button
                    type="button"
                    onClick={() => setView("text")}
                    className={
                      "text-sm px-3 py-1 rounded " +
                      (view === "text"
                        ? "bg-white border border-slate-300"
                        : "text-slate-500 hover:text-slate-800")
                    }
                    data-testid="email-preview-view-text"
                  >
                    Plain text
                  </button>
                </div>

                {view === "html" ? (
                  <iframe
                    title="email-preview-iframe"
                    srcDoc={preview.html || ""}
                    sandbox=""
                    className="w-full h-[720px] bg-white"
                    data-testid="email-preview-html-frame"
                  />
                ) : (
                  <pre
                    className="p-4 whitespace-pre-wrap text-sm text-slate-800 font-mono bg-white"
                    data-testid="email-preview-text-body"
                  >
                    {preview.text}
                  </pre>
                )}
              </>
            )}
          </section>
        </div>
      </main>
    </RetailerLayout>
  );
}
